<template>
  <div v-if="showPayrollPopup" class="payroll-popup">
    <div v-if="showPayrollPopup" class="popup-container">
      <div class="popup-content">
        <div class="payroll-title">Payroll Time Entry</div>
        <div class="payroll-content">
          <div class="input-group">
            <label for="date">Date</label>
            <input type="date" v-model="selectedDate" />
          </div>
          <div class="table-header">
            <span class="name-column">Name</span>
            <span class="reg-column">Reg</span>
            <span class="ot-column">OT</span>
            <span class="action-column"></span>
          </div>
          <div
            class="employee-inputs"
            v-for="(employee, index) in employeeData"
            :key="index"
          >
            <input
              type="text"
              v-model="employee.name"
              class="name-column"
              disabled
            />
            <input type="text" v-model="employee.reg" class="reg-column" />
            <input type="text" v-model="employee.ot" class="ot-column" />
            <div class="action-column">
              <button class="small-button" @click="editEmployee(employee)">
                <i class="fas fa-pencil"></i>
              </button>
            </div>
          </div>
          <div class="button-row">
            <button class="add-button" @click="showAddEmployeeModal = true">
              + Add Employee
            </button>
            <div class="action-buttons">
              <button @click="generateEmail">Go</button>
              <button @click="closePopup">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Employee Modal -->
    <div v-if="showAddEmployeeModal" class="modal">
      <div class="modal-content">
        <div class="modal-title">{{ isEditing ? 'Edit' : 'Add' }} Employee</div>
        <div class="modal-form">
          <div class="form-group">
            <label for="first">First Name</label>
            <input type="text" v-model="newEmployee.first" id="first" />
          </div>
          <div class="form-group">
            <label for="middle">Middle Name (optional)</label>
            <input type="text" v-model="newEmployee.middle" id="middle" />
          </div>
          <div class="form-group">
            <label for="last">Last Name</label>
            <input type="text" v-model="newEmployee.last" id="last" />
          </div>
          <div class="form-group">
            <label for="employeeId">Employee ID</label>
            <input
              type="number"
              v-model="newEmployee.employeeId"
              id="employeeId"
              :disabled="isEditing"
            />
          </div>
          <div class="form-group">
            <label for="typicalHours">Typical Hours</label>
            <input
              type="number"
              v-model="newEmployee.typicalHours"
              id="typicalHours"
              step="0.5"
              min="0"
            />
          </div>
          <div class="modal-buttons">
            <button @click="saveEmployee">Save</button>
            <button @click="cancelAddEmployee">Cancel</button>
            <button
              v-if="isEditing"
              @click="deleteEmployee"
              class="delete-button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import axios
import axios from '@/axiosConfig';

export default {
  name: 'PayrollPopup',
  props: {
    showPayrollPopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      selectedDate: new Date().toISOString().substr(0, 10),
      employeeData: [],
      showAddEmployeeModal: false,
      isEditing: false,
      newEmployee: {
        id: null,
        first: '',
        middle: '',
        last: '',
        employeeId: null,
        typicalHours: 40,
      },
      editingEmployeeIndex: -1,
    };
  },
  created() {
    this.fetchEmployeeData();
  },
  watch: {
    showPayrollPopup(newVal) {
      if (newVal) {
        this.fetchEmployeeData();
      }
    },
  },
  methods: {
    async fetchEmployeeData() {
      try {
        const response = await axios.get('/api/payroll/employees');
        // Map database employee data to the format expected by the PayrollPopup component
        this.employeeData = response.data.map((emp) => ({
          id: emp.id,
          employeeId: emp.employeeId,
          name: emp.first,
          reg: emp.typicalHours.toString(),
          ot: '',
          first: emp.first,
          middle: emp.middle || '',
          last: emp.last || '',
          typicalHours: emp.typicalHours,
        }));
      } catch (error) {
        console.error('Error fetching employee data:', error);
      }
    },

    editEmployee(employee) {
      this.isEditing = true;
      this.editingEmployeeIndex = this.employeeData.findIndex(
        (e) => e.id === employee.id,
      );
      this.newEmployee = {
        id: employee.id,
        first: employee.first,
        middle: employee.middle || '',
        last: employee.last || '',
        employeeId: employee.employeeId,
        typicalHours: employee.typicalHours,
      };
      this.showAddEmployeeModal = true;
    },

    async saveEmployee() {
      try {
        if (this.isEditing) {
          // Update existing employee
          await axios.put(
            `/api/payroll/employees/${this.newEmployee.id}`,
            this.newEmployee,
          );

          // Update local data
          const index = this.editingEmployeeIndex;
          if (index !== -1) {
            this.employeeData[index] = {
              ...this.employeeData[index],
              id: this.newEmployee.id,
              name: this.newEmployee.first,
              first: this.newEmployee.first,
              middle: this.newEmployee.middle,
              last: this.newEmployee.last,
              employeeId: this.newEmployee.employeeId,
              typicalHours: this.newEmployee.typicalHours,
              reg: this.newEmployee.typicalHours.toString(),
            };
          }
        } else {
          // Add new employee
          const response = await axios.post(
            '/api/payroll/employees',
            this.newEmployee,
          );
          const newEmp = response.data;

          // Add to local data
          this.employeeData.push({
            id: newEmp.id,
            name: newEmp.first,
            reg: newEmp.typicalHours.toString(),
            ot: '',
            first: newEmp.first,
            middle: newEmp.middle,
            last: newEmp.last,
            employeeId: newEmp.employeeId,
            typicalHours: newEmp.typicalHours,
          });
        }

        this.cancelAddEmployee();
      } catch (error) {
        console.error('Error saving employee:', error);
      }
    },

    async deleteEmployee() {
      if (!confirm('Are you sure you want to delete this employee?')) {
        return;
      }

      try {
        await axios.delete(`/api/payroll/employees/${this.newEmployee.id}`);

        // Remove from local data
        const index = this.editingEmployeeIndex;
        if (index !== -1) {
          this.employeeData.splice(index, 1);
        }

        this.cancelAddEmployee();
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    },

    cancelAddEmployee() {
      this.showAddEmployeeModal = false;
      this.isEditing = false;
      this.newEmployee = {
        id: null,
        first: '',
        middle: '',
        last: '',
        employeeId: null,
        typicalHours: 40,
      };
      this.editingEmployeeIndex = -1;
    },

    generateTabularText() {
      // Parse the selectedDate in local time
      const selectedDate = new Date(this.selectedDate);

      // Set periodEnd to one day before selectedDate (local time)
      const periodEnd = new Date(selectedDate);
      periodEnd.setDate(periodEnd.getDate() - 1);

      // Set periodStart to six days before periodEnd (local time)
      const periodStart = new Date(periodEnd);
      periodStart.setDate(periodStart.getDate() - 6);

      // Format dates to ensure they are in the expected format
      const periodStartFormatted = periodStart.toISOString().split('T')[0];
      const periodEndFormatted = periodEnd.toISOString().split('T')[0];
      const selectedDateFormatted = selectedDate.toISOString().split('T')[0];

      console.log(`Selected Date: ${selectedDateFormatted}`);
      console.log(`Period Start: ${periodStartFormatted}`);
      console.log(`Period End: ${periodEndFormatted}`);

      let lines = [];
      lines.push(`GR357 hours for ${selectedDateFormatted}\n\n`);
      lines.push(
        `Here are our hours for the period ${periodStartFormatted} to ${periodEndFormatted} for GR357\n\n`,
      );
      lines.push('Name         Reg     OT    Total\n');

      this.employeeData.forEach(({ name, reg, ot }) => {
        try {
          let regDisplay = reg;
          let otDisplay = ot || '0.0';
          let totalDisplay = '';

          if (
            reg.trim().toLowerCase() === 'salary' ||
            ot.trim().toLowerCase() === 'salary'
          ) {
            regDisplay = 'sal.';
            otDisplay = '0.0';
            totalDisplay = 'sal.';
          } else {
            const regVal = parseFloat(reg) || 0;
            const otVal = parseFloat(ot) || 0;
            const total = regVal + otVal;
            regDisplay = regVal.toFixed(1).padStart(4, '0');
            otDisplay = otVal.toFixed(1).padStart(4, '0');
            totalDisplay = total.toFixed(1).padStart(4, '0');
          }

          lines.push(
            `${name.padEnd(10)}${regDisplay.padStart(7)}${otDisplay.padStart(7)}${totalDisplay.padStart(7)}\n`,
          );
        } catch (error) {
          lines.push(`${name.padEnd(10)}Error___Error___Error\n`);
        }
      });

      lines.push('\nThank you.\n');
      return lines.join('');
    },

    // Define the generateEmail function
    async generateEmail() {
      // Assuming this.generateTabularText() is a method in your context
      const tabularText = this.generateTabularText();
      console.log('email body:', tabularText);
      // const encodedBody = encodeURIComponent(tabularText);
      const encodedBody = tabularText;

      // Create the email data object
      const emailData = {
        subject: `GR357 hours for ${this.selectedDate}`,
        from: 'billing@bigpropertymanagement.com',
        replyTo: 'billing@bigpropertymanagement.com',
        body: encodedBody,
        bcc: 'billing@bigpropertymanagement.com',
        recipients: 'service@payentry.com',
        // recipients: 'eric@bigpropertymanagement.com',
      };

      // Call the sendEmail function to send the email
      try {
        await this.sendEmail(emailData);
        // console.log('Email request sent successfully');
      } catch (error) {
        console.error('Failed to send email:', error);
      }
    },

    // Example function to send an email using Axios
    async sendEmail(emailData) {
      console.log('emailData', emailData);
      try {
        const response = await axios.post('/api/send-email', emailData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('Email sent successfully:', response.data);
        this.$emit('update-show-payroll-popup', false);
      } catch (error) {
        console.error(
          'Error sending email:',
          error.response ? error.response.data : error.message,
        );
      }
    },

    closePopup() {
      this.$emit('update-show-payroll-popup', false);
    },
  },
};
</script>

<style scoped>
.payroll-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}
.popup-container {
  background: white;
  border-radius: 8px;
  width: 475px;
  box-shadow: 7px 7px 5px 2px rgb(0 0 0 / 22%);
}

.popup-content {
  width: 100%;
}

.input-group {
  margin-bottom: 15px;
}

.popup-container input {
  margin-left: 8px;
  border-radius: 4px;
  border: 1pt solid #ddd;
  padding: 8px;
  width: 25%;
}

.table-header,
.employee-inputs {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
}
.name-column {
  flex: 2;
}
.reg-column,
.ot-column {
  flex: 1;
}
.action-column {
  width: 40px;
  display: flex;
  justify-content: center;
}
button {
  margin-top: 10px;
  margin-right: 10px;
  border-radius: 5px;
  color: white;
  background: #a080a8;
  border: 1pt solid #666;
  padding: 8px;
  width: 60px;
}

.small-button {
  width: 30px;
  height: 30px;
  padding: 2px;
  margin: 0;
  color: #333;
  font-size: 12px;
  background: none;
  border: 1px solid #ddd;
}

.add-button {
  background: #8c7093;
  color: #fff;
  width: auto;
  padding: 8px 16px;
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.payroll-title,
.modal-title {
  margin-bottom: 10px;
  background: #6e5875;
  color: #eee;
  padding: 10px;
  font-size: 1.2em;
  text-align: center;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}
.modal-title {
  margin-top: -10px;
  padding-top: 22px;
}

.payroll-content {
  padding: 20px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 80%;
  max-width: 500px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 0;
  overflow: hidden;
}

.modal-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 0.75em;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.delete-button {
  background: #d9534f;
  color: white;
}
</style>
